import React from 'react';
import styled from 'styled-components';

import useTranslation from 'hooks/use-translation';

import { palette } from './palette';
import { Container, Section, SectionHead } from './primitives';
import { useReveal } from './use-reveal';

export function OrderingStepsSection(): JSX.Element {
  const { t } = useTranslation();
  const { ref, inView } = useReveal<HTMLDivElement>();

  const steps = [
    {
      title: t('home.redesign.stepFindTitle', 'Find your dispensary'),
      body: t(
        'home.redesign.stepFindBody',
        "Search by location. See real-time menus, ratings, hours, and what's in stock right now."
      ),
    },
    {
      title: t('home.redesign.stepOrderTitle', 'Order from their menu'),
      body: t(
        'home.redesign.stepOrderBody',
        'Add items to your cart. Choose pickup or delivery. Your dispensary prepares your order.'
      ),
    },
    {
      title: t('home.redesign.stepPickupTitle', 'Pick up or get delivery'),
      body: t(
        'home.redesign.stepPickupBody',
        "Skip the line — your order's ready when you arrive. Or get it delivered from dispensaries that offer it."
      ),
    },
  ];

  return (
    <Section>
      <Container>
        <SectionHead
          first={t('home.redesign.orderingFirst', 'Ordering')}
          rest={t('home.redesign.orderingRest', 'made easy')}
          sub={t(
            'home.redesign.orderingSub',
            'The highest-rated dispensaries in your area, with real-time menus and online ordering'
          )}
        />
        <Steps ref={ref} $in={inView}>
          {steps.map((step, index) => (
            <Step key={step.title}>
              <Num>{index + 1}</Num>
              <h4>{step.title}</h4>
              <p>{step.body}</p>
            </Step>
          ))}
        </Steps>
      </Container>
    </Section>
  );
}

const Steps = styled.div<{ $in: boolean }>`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 36px;

  ${({ theme }) => theme.breakpoints.down(`xs`)} {
    grid-template-columns: 1fr;
  }

  > div {
    opacity: ${({ $in: inView }) => (inView ? 1 : 0)};
    transform: ${({ $in: inView }) => (inView ? `none` : `translateY(20px)`)};
    transition: opacity 0.8s ease, transform 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  > div:nth-child(2) {
    transition-delay: 0.18s;
  }

  > div:nth-child(3) {
    transition-delay: 0.36s;
  }

  @media (prefers-reduced-motion: reduce) {
    > div {
      opacity: 1;
      transform: none;
      transition: none;
    }
  }
`;

const Step = styled.div`
  text-align: center;
  padding: 0 12px;

  h4 {
    margin: 0 0 10px;
    font-size: 22px;
    color: ${palette.cream};
  }

  p {
    margin: 0;
    color: ${palette.creamDim};
    font-size: 15px;
    line-height: 1.55;
  }
`;

const Num = styled.div`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: ${palette.gold};
  color: ${palette.textOnGold};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  margin: 0 auto 18px;
`;
